import { JSONPath } from 'jsonpath-plus';
import { debounce } from 'lodash-es';
import { useEffect, useMemo, useState } from 'react';

const evaluateJsonPath = (obj: unknown, path: string): string => {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const results = JSONPath({ path, json: obj as any }) as any[];

    if (results.length === 0) {
      return `No results found for path: ${path}`;
    }

    if (results.length === 1) {
      const result = results[0];
      return typeof result === 'object'
        ? JSON.stringify(result, null, 2)
        : String(result);
    }

    return JSON.stringify(results, null, 2);
  } catch (e) {
    return `Error evaluating path: ${e instanceof Error ? e.message : 'Unknown error'}`;
  }
};

export const useJsonPath = (value: string) => {
  const [jsonPathResult, setJsonPathResult] = useState<string>('');

  const debouncedFn = useMemo(
    () =>
      debounce((jsonPath: string) => {
        if (!jsonPath.trim()) {
          setJsonPathResult('');
          return;
        }

        try {
          const jsonData = JSON.parse(value);
          const result = evaluateJsonPath(jsonData, jsonPath);
          setJsonPathResult(result);
        } catch (e) {
          setJsonPathResult('{}');
        }
      }, 200),
    [value]
  );

  useEffect(
    () => () => {
      debouncedFn.cancel();
    },
    [debouncedFn]
  );

  return { jsonPathResult, handleInputChange: debouncedFn };
};
